// Turn whatever a failed call threw into something an operator can act on.
//
// Lives here rather than in @ezhire/sdk because packages/admin-features does not depend on the
// module SDK, and the bug this fixes is repo-wide — hundreds of call sites across apps/web,
// modules and packages reported database failures as the single word "unknown".
//
// Why: supabase-js does NOT throw Errors. A PostgrestError is a plain object
// `{ message, details, hint, code }`, so `err instanceof Error` is false for the entire class of
// failure this product actually produces. Found live — creating a journey showed
// "Save failed — unknown" when the real error was
// `42501 new row violates row-level security policy for table "journeys"`.

interface MessageLike {
  message?: unknown;
  code?: unknown;
  details?: unknown;
  hint?: unknown;
}

/**
 * DELIBERATELY message + code ONLY.
 *
 * An earlier version also appended `details` and `hint`, which is a data leak at every call site.
 * Postgres puts the whole offending tuple in `errdetail` for CHECK and NOT NULL violations and
 * PostgREST forwards it verbatim, so a failed save rendered things like:
 *
 *   "… (code 23514 · Failing row contains (uuid-1, agent@ezhire.com, S3cretP@ss!, 2026-08-26).)"
 *   "… (code 23505 · Key (email)=(ceo@rival.com) already exists.)"
 *
 * — a plaintext credential and another customer's address, in a toast, on screens that had
 * previously shown only the word "unknown". Caught in pre-push review before it shipped.
 *
 * Nothing of value is lost: the error that motivated this helper reads
 * `new row violates row-level security policy for table "journeys" (code 42501)` with message and
 * code alone. `details`/`hint` belong in a server log, not a UI string that every caller renders.
 */
export function describeError(err: unknown, fallback = 'unknown error'): string {
  if (typeof err === 'string' && err.trim()) return err;

  if (err instanceof Error) {
    if (err.message) return err.message;
    // AggregateError (and any Error subclass with an empty message) used to degrade to the
    // fallback, losing which failure it was. Promise.any produces these.
    const first = (err as AggregateError).errors?.[0];
    const inner = first instanceof Error ? first.message : '';
    return inner ? `${err.name}: ${inner}` : err.name || fallback;
  }

  if (err && typeof err === 'object') {
    // A getter that throws would otherwise propagate out of a formatter and take down the render
    // that was trying to report an error. The old idiom never read .message off a non-Error, so
    // this path is new and has to be defensive.
    try {
      const e = err as MessageLike;
      const message = typeof e.message === 'string' ? e.message.trim() : '';
      // The SQLSTATE decides what the reader does next: 42501 is "you lack permission",
      // 23505 is "already exists". Worth carrying; it names no data.
      const code = typeof e.code === 'string' ? e.code.trim() : '';
      if (message) return code ? `${message} (code ${code})` : message;
      // No message but a code still beats the fallback — it is searchable. Guard the empty
      // fallback so this cannot return a string with a leading space.
      if (code) return fallback ? `${fallback} (code ${code})` : `code ${code}`;
    } catch {
      return fallback;
    }
  }

  return fallback;
}
